import type { ExpressCheckoutElementProps } from "@stripe/react-stripe-js";
import {
  ExpressCheckoutElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { t } from "ttag";

import type { OnboardingUser } from "@/features/onboarding/models";
import type { UrlParamsSlice } from "@/features/onboarding/stores";
import { useCreateOnboardingPayments } from "@/features/payments/hooks";
import {
  PAYMENT_METHOD_TYPE,
  PAYMENT_REDIRECT_METHOD_KEY,
} from "@/shared/domains";
import type { Coupon, Price } from "@/shared/domains";
import { errorToast } from "@/shared/ui";

type ApplePayButtonProps = Pick<
  ExpressCheckoutElementProps,
  "onClick" | "className"
> & {
  price: Price;
  user: Pick<OnboardingUser, "code" | "state" | "shipping">;
  discount?: Coupon["code"];
  metadata?: UrlParamsSlice["urlParams"];
};

export const ApplePayButton = ({
  price,
  user,
  discount,
  metadata,
  ...props
}: ApplePayButtonProps) => {
  const stripe = useStripe();
  const elements = useElements();

  const createPurchasesMutation = useCreateOnboardingPayments();

  if (!stripe || !elements) {
    return null;
  }

  const handleConfirm = async () => {
    const paymentIntents = await createPurchasesMutation.mutateAsync({
      prices: [price.id],
      userCode: user.code,
      state: user.state,
      shipping: user.shipping,
      discount,
      metadata,
    });

    const returnUrl = new URL(window.location.href);
    returnUrl.searchParams.set(
      PAYMENT_REDIRECT_METHOD_KEY,
      PAYMENT_METHOD_TYPE.APPLE_PAY,
    );

    const clientSecret = paymentIntents[0]?.clientSecret;

    if (!clientSecret) {
      return errorToast(t`An error occurred while processing your payment.`);
    }

    const { error } = await stripe.confirmPayment({
      elements,
      clientSecret,
      confirmParams: {
        return_url: returnUrl.toString(),
      },
    });

    if (error) {
      return errorToast(
        error.message ?? t`An error occurred while processing your payment.`,
      );
    }
  };

  return (
    <ExpressCheckoutElement
      onConfirm={handleConfirm}
      onLoadError={() =>
        errorToast(
          t`An error occurred while loading Apple Pay, please select another payment method.`,
        )
      }
      options={{
        buttonHeight: 46,
        paymentMethods: {
          applePay: "always",
          link: "never",
        },
      }}
      {...props}
    />
  );
};
